// src/screens/academique/Labellisation.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Paper,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Button
} from "@material-ui/core";
import {
  EmojiEvents as EmojiEventsIcon,
  PlayCircle as PlayCircleIcon,
  Download as DownloadIcon,
  CalendarToday as CalendarTodayIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  Quiz as QuizIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Circle as CircleIcon
} from '@mui/icons-material';
import FormationMainContainer from '../../components/FormationMainContainer';
import LocalDataService from '../../services/LocalDataService';

const Labellisation = () => {
  const [loading, setLoading] = useState(true);
  const [availableTests, setAvailableTests] = useState([]);
  const [student, setStudent] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Récupérer les données depuis le service local
        const userData = LocalDataService.getUser();
        const testsData = LocalDataService.getLabelingTests(userData.data);
        
        setStudent(userData.dataSpecial);
        setAvailableTests(testsData || []);
        
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        setAvailableTests([]);
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };
  
  const getActiveSession = (test) => {
    if (!test.sessions) return null;
    const now = new Date();
    return test.sessions.find(s => {
      if (s.status !== 'active') return false;
      const startDate = new Date(s.scheduled_date);
      const endDate = s.scheduled_end_date ? new Date(s.scheduled_end_date) : null;
      if (endDate) {
        return now >= startDate && now <= endDate;
      }
      return now >= startDate;
    });
  };
  
  const getSubmittedResult = (test) => {
    if (test.submitted_results && test.submitted_results.length > 0) {
      return test.submitted_results[0];
    }
    return null;
  };

  const getStatusInfo = (test) => {
    const activeSession = getActiveSession(test);
    const submittedResult = getSubmittedResult(test);
    const isSubmitted = test.has_submitted || !!submittedResult;
    const isActive = !!activeSession && !isSubmitted;
    
    if (isActive) {
      return { status: 'active', label: 'Disponible', color: '#28A745', icon: <CircleIcon sx={{ fontSize: 8 }} /> };
    }
    if (isSubmitted) {
      const passed = submittedResult?.passed;
      return { 
        status: passed ? 'passed' : 'failed', 
        label: passed ? 'Réussi' : 'Non réussi', 
        color: passed ? '#28A745' : '#DC3545',
        icon: passed ? <CheckCircleIcon sx={{ fontSize: 12 }} /> : <CancelIcon sx={{ fontSize: 12 }} />
      };
    }
    return { status: 'planned', label: 'Planifié', color: '#6C757D', icon: <PendingIcon sx={{ fontSize: 12 }} /> };
  };

  const openLabelingTest = (test, session) => {
    // Logique pour ouvrir le test
    console.log('Opening test:', test, session);
    // Vous pouvez déclencher un événement ou utiliser une modal
    window.dispatchEvent(new CustomEvent('openLabelingTest', { detail: { test, session } }));
  };

  const downloadResult = (testId) => {
    // Logique pour télécharger le résultat
    console.log('Downloading result for test:', testId);
    window.dispatchEvent(new CustomEvent('downloadTestResult', { detail: { testId } }));
  };

  if (loading) {
    return (
      <FormationMainContainer maxWidth="xl" sx={{ py: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Stack spacing={2} alignItems="center">
            <CircularProgress />
            <Typography variant="body2" color="textSecondary">
              Chargement des tests de labellisation...
            </Typography>
          </Stack>
        </Box>
      </FormationMainContainer>
    );
  }

  if (!availableTests || availableTests.length === 0) {
    return (
      <FormationMainContainer maxWidth="xl" sx={{ py: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Paper sx={{ textAlign: 'center', p: 8, borderRadius: '16px', bgcolor: '#F9FAFB', maxWidth: 500, mx: 'auto' }}>
            <EmojiEventsIcon sx={{ fontSize: 64, color: '#9E9E9E', opacity: 0.5, mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Aucun test de labellisation disponible
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Revenez plus tard pour passer vos certifications
            </Typography>
          </Paper>
        </Box>
      </FormationMainContainer>
    );
  }

  return (
    <FormationMainContainer maxWidth="xl" sx={{ py: 3 }}>
      {/* En-tête */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="800" gutterBottom display="flex" alignItems="center" gap={1}>
          <EmojiEventsIcon sx={{ color: '#1976D2', fontSize: 32 }} />
          Évaluation
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Passez vos tests 
        </Typography>
      </Box>

      {/* Grille des tests */}
      <Grid container spacing={3}>
        {availableTests.map((test) => {
          const activeSession = getActiveSession(test);
          const submittedResult = getSubmittedResult(test);
          const isSubmitted = test.has_submitted || !!submittedResult;
          const isActive = !!activeSession && !isSubmitted;
          const statusInfo = getStatusInfo(test);
          
          return (
            <Grid item xs={12} md={6} lg={4} key={test.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '16px',
                  transition: 'all 0.3s ease',
                  borderLeft: isSubmitted ? '4px solid #6C757D' : 'none',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                {/* En-tête */}
                <Box
                  sx={{
                    p: 2.5,
                    background: 'linear-gradient(135deg, rgba(0,86,164,0.06), rgba(23,162,184,0.04))',
                    borderBottom: '1px solid rgba(0,0,0,0.06)'
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <EmojiEventsIcon sx={{ color: '#FD7E14' }} />
                      <Typography variant="subtitle1" fontWeight="700">
                        {test.title}
                      </Typography>
                    </Stack>
                    <Chip
                      icon={statusInfo.icon}
                      label={statusInfo.label}
                      size="small"
                      sx={{
                        bgcolor: `${statusInfo.color}15`,
                        color: statusInfo.color,
                        fontWeight: 600,
                        '& .MuiChip-icon': { color: statusInfo.color }
                      }}
                    />
                  </Stack>
                  {test.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.5 }}>
                      {test.description}
                    </Typography>
                  )}
                </Box>

                <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                  {/* Informations */}
                  <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                    <Chip
                      icon={<QuizIcon />}
                      label={`${test.question_count} questions`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      icon={<PeopleIcon />}
                      label={test.class_names}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>

                  {/* Sessions */}
                  {test.sessions && test.sessions.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" fontWeight="700" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                        Sessions disponibles
                      </Typography>
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {test.sessions.map((session) => (
                          <Paper
                            key={session.id}
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              bgcolor: session.status === 'active' ? 'rgba(40,167,69,0.06)' : 'transparent',
                              borderColor: session.status === 'active' ? 'rgba(40,167,69,0.3)' : 'rgba(0,0,0,0.08)'
                            }}
                          >
                            <Stack spacing={0.5}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <CalendarTodayIcon sx={{ fontSize: 14, color: '#0056A4' }} />
                                <Typography variant="caption">
                                  Début: {formatDate(session.scheduled_date)}
                                </Typography>
                                {session.status === 'active' && (
                                  <Chip
                                    label="En cours"
                                    size="small"
                                    sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#28A745', color: '#FFFFFF' }}
                                  />
                                )}
                              </Stack>
                              {session.scheduled_end_date && (
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <ScheduleIcon sx={{ fontSize: 14, color: '#0056A4' }} />
                                  <Typography variant="caption">
                                    Fin: {formatDate(session.scheduled_end_date)}
                                  </Typography>
                                </Stack>
                              )}
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {/* Score obtenu */}
                  {isSubmitted && submittedResult && (
                    <Box
                      sx={{
                        p: 1.5,
                        mb: 2,
                        bgcolor: submittedResult.passed ? 'rgba(40,167,69,0.08)' : 'rgba(220,53,69,0.08)',
                        borderRadius: 2,
                        textAlign: 'center'
                      }}
                    >
                      <Typography variant="h5" fontWeight="800" color={submittedResult.passed ? '#28A745' : '#DC3545'}>
                        {submittedResult.global_score}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Score obtenu
                      </Typography>
                    </Box>
                  )}
                </CardContent>

                {/* Actions */}
                <Box sx={{ p: 2.5, pt: 0 }}>
                  {isActive && activeSession && (
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<PlayCircleIcon />}
                      onClick={() => openLabelingTest(test, activeSession)}
                      sx={{
                        bgcolor: '#28A745',
                        '&:hover': { bgcolor: '#218838' }
                      }}
                    >
                      Démarrer le test
                    </Button>
                  )}
                  
                  {!isActive && !isSubmitted && (
                    <Box sx={{ textAlign: 'center', py: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        <PendingIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                        En attente d'ouverture
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </FormationMainContainer>
  );
};

export default Labellisation;